import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useMotionValue, useReducedMotion } from "framer-motion";
import ViewCompanyEarningsLoader from "../Charts/ViewCompanyEarningsLoader";
import HomeTab from "./tabs/HomeTab";
import HubTab from "./tabs/HubTab";
import SearchTab from "./tabs/SearchTab";
import CalendarTab from "./tabs/CalendarTab";
import { hapticSelection } from "../../../lib/haptics";
import { useTouchLayout } from "../../../hooks/useTouchLayout";
import {
  settleViews,
  ViewPanes,
  ViewSwitch,
  viewIndex,
  type ViewOption,
} from "../../polish/ViewSwitch";
import { tonalTokens } from "../../polish/tokens";
import { viewFromState, viewState } from "./earningsNavigation";

export type EarningsTab = "home" | "hub" | "calendar" | "search";
type EarningsView = Exclude<EarningsTab, "search">;

// Your watchlist's dates, what analysts did after each report, and the whole
// market are peers, not a drill-down, so they share one switch. Search stays
// what it is — a drill-down from the watchlist with its own back button — so
// the switch hides while it's open.
const VIEWS: ViewOption<EarningsView>[] = [
  { key: "hub", label: "Earnings Hub" },
  { key: "home", label: "Watchlist" },
  { key: "calendar", label: "Calendar" },
];

interface EarningsPageProps {
  initialTab?: EarningsView;
}

/**
 * /earnings: the Earnings Hub feed, the Watchlist and the market Calendar, on
 * the tonal view switch; on touch the three are panes a horizontal swipe
 * moves between.
 */
const EarningsPage: React.FC<EarningsPageProps> = ({ initialTab = "hub" }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTouch = useTouchLayout();
  const reduced = useReducedMotion() === true;
  const tokens = tonalTokens(theme.palette.mode === "dark");
  // Back from a report or a company page returns to the view it left.
  const [activeTab, setActiveTab] = useState<EarningsTab>(
    () => viewFromState(location.state) ?? initialTab,
  );
  // Search opens from the watchlist, so that's where the panes rest under it.
  const view: EarningsView = activeTab === "search" ? "home" : activeTab;
  const progress = useMotionValue(viewIndex(VIEWS, view));
  // The header row's right side: the Earnings Hub's list controls render here.
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);

  const ticker = searchParams.get("ticker");

  const { pathname, search, state } = location;
  const handleTabChange = useCallback(
    (tab: EarningsTab) => {
      hapticSelection();
      setActiveTab(tab);
      if (tab === "search") return;
      // The views share one URL, so the history entry remembers which shows.
      navigate(
        { pathname, search },
        {
          replace: true,
          state: { ...((state as object | null) ?? {}), ...viewState(tab) },
        },
      );
    },
    [navigate, pathname, search, state],
  );

  // Views swap inside one route, so the document keeps whatever scroll
  // position the previous view had. Arriving at Search already scrolled past
  // its search box — the one thing on the page — meant scrolling back up.
  const shown = useRef(activeTab);
  useEffect(() => {
    const controls = settleViews(progress, viewIndex(VIEWS, view), reduced);
    if (shown.current !== activeTab) {
      shown.current = activeTab;
      window.scrollTo(0, 0);
    }
    return () => controls?.stop();
  }, [activeTab, view, progress, reduced]);

  if (ticker) {
    return <ViewCompanyEarningsLoader />;
  }

  return (
    <Box
      data-testid="earnings-page"
      sx={{
        backgroundColor: theme.palette.mode === "dark" ? "#1a1a1a" : "#ffffff",
        // dvh, not vh: under the native shell NativeTabLayout already reserves
        // the 56px tab bar plus its safe area, so 100vh overflows by exactly
        // that much and leaves the last row unreachable.
        minHeight: "100dvh",
        pb: 6,
      }}
    >
      {activeTab === "search" && (
        <SearchTab onBack={() => handleTabChange("home")} />
      )}
      {activeTab !== "search" && (
        <>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              pt: { xs: 2, md: 3 },
              pb: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ flex: isTouch ? 1 : "none", minWidth: 0 }}>
              <ViewSwitch
                views={VIEWS}
                view={view}
                onChange={handleTabChange}
                progress={progress}
                fullWidth={isTouch}
                tokens={tokens}
                label="Earnings view"
              />
            </Box>
            {!isTouch && (
              <Box
                ref={setHeaderSlot}
                data-testid="earnings-header-slot"
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  minWidth: 0,
                }}
              />
            )}
          </Box>
          <ViewPanes
            panes={[
              {
                key: "hub",
                content: (
                  <HubTab active={view === "hub"} headerSlot={headerSlot} />
                ),
              },
              {
                key: "home",
                content: <HomeTab onTabChange={handleTabChange} />,
              },
              { key: "calendar", content: <CalendarTab /> },
            ]}
            view={view}
            progress={progress}
            swipeable={isTouch}
            reduced={reduced}
            onSwipe={handleTabChange}
          />
        </>
      )}
    </Box>
  );
};

export default EarningsPage;
